/**
 * Error boundary wrapper for plugin renderers.
 * Catches plugin renderer crashes and displays fallback UI.
 * Keeps the core todo list functional when a plugin crashes.
 */

import React, { Component, ErrorInfo } from 'react';

export interface PluginErrorBoundaryProps {
  pluginId?: string;
  fallback?: React.ReactNode;
  children: React.ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface PluginErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

// Matches webpack's ChunkLoadError ("Loading chunk 648 failed") and the
// native ESM equivalent. Used to auto-reload once per session so users
// with a stale index.html (e.g. held by a CDN edge after a deploy) pick
// up the fresh bundle without manually purging their cache.
function isChunkLoadError(error: Error | null): boolean {
  if (!error) return false;
  if (error.name === 'ChunkLoadError') return true;
  const msg = error.message || '';
  return /Loading chunk \S+ failed|Failed to fetch dynamically imported module/.test(msg);
}

const CHUNK_RELOAD_SENTINEL = 'family-os-chunk-reload';

export class PluginErrorBoundary extends Component<
  PluginErrorBoundaryProps,
  PluginErrorBoundaryState
> {
  constructor(props: PluginErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): PluginErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    console.error(
      `[PluginErrorBoundary] Plugin ${this.props.pluginId || 'unknown'} crashed:`,
      error,
      errorInfo
    );
    this.props.onError?.(error, errorInfo);

    // Auto-reload once per session on ChunkLoadError. Most chunk failures
    // come from a deploy swapping hashed filenames out from under a tab
    // that cached an older index.html; a single reload pulls fresh HTML
    // with the current chunk hashes. The sessionStorage sentinel guards
    // against reload loops — if the reload doesn't fix it (offline, real
    // network failure), the boundary UI takes over on the second attempt.
    if (isChunkLoadError(error)) {
      try {
        if (typeof window !== 'undefined' && window.sessionStorage) {
          if (!window.sessionStorage.getItem(CHUNK_RELOAD_SENTINEL)) {
            window.sessionStorage.setItem(CHUNK_RELOAD_SENTINEL, String(Date.now()));
            window.location.reload();
          }
        }
      } catch {
        // sessionStorage unavailable; fall through to the normal error UI.
      }
    }
  }

  componentDidUpdate(prevProps: PluginErrorBoundaryProps): void {
    // Reset error state when children change (e.g., after retry with a new component)
    if (this.state.hasError && prevProps.children !== this.props.children) {
      this.setState({ hasError: false, error: null });
    }
  }

  handleRetry = (): void => {
    this.setState({ hasError: false, error: null });
  };

  render(): React.ReactNode {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="plugin-error" role="alert" data-testid="plugin-error-boundary">
          <span className="plugin-error__message">
            Plugin failed to render
          </span>
          <button
            className="plugin-error__retry"
            onClick={this.handleRetry}
            type="button"
          >
            Retry
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}
